/* Author: omkar khamkar 16-02-2026 */
import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, OnDestroy, ViewEncapsulation, TemplateRef, ViewChild} from '@angular/core';
import { forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { WorkflowService } from 'src/app/services/workflow.service';
import { svgs } from 'src/assets/export.assets';
import { CommonConfirmationModalData } from 'src/app/components/common/common-confirmation-modal/common-confirmation-modal.component.model';
import { NoteEntry } from './common-notes.model';
import { TableColumn } from 'src/app/components/common/common-table/common-table.component';

@Component({
  selector: 'app-common-notes',
  standalone: false,
  templateUrl: './common-notes.component.html',
  styleUrls: ['./common-notes.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CommonNotesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() applicationId = '';
  @Input() applicationNumber = '';

  @Output() noteSubmitted = new EventEmitter<any>();
  @Output() notesChanged = new EventEmitter<{
    applicationId: string;
    unreadNotes: number;
    totalNotes: number;
  }>();

  // Added by Vipul on 26-09-2026
  @ViewChild('selectTemplate', { static: true })
  selectTemplate!: TemplateRef<any>;

  @ViewChild('nameTemplate', { static: true })
  nameTemplate!: TemplateRef<any>;

  @ViewChild('noteTemplate', { static: true })
  noteTemplate!: TemplateRef<any>;

  @ViewChild('createdTemplate', { static: true })
  createdTemplate!: TemplateRef<any>;

  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<any>;
  // End by Vipul on 26-09-2026

  internalState = { text: '', isExpanded: true };
  externalState = { text: '', isExpanded: false };

  internalNotesHistory: NoteEntry[] = [];
  externalNotesHistory: NoteEntry[] = [];

  // Added by Vipul on 26-09-2026
  columns: TableColumn[] = [];

  private initializeColumns(): void {
    this.columns = [
      {
        key: 'select',
        label: 'Select',
        cellType: 'template',
        cellTemplate: this.selectTemplate,
        width: '10%',
        sortable: false,
        hideable: false,
        hideMenu: true
      },
      {
        key: 'name',
        label: 'Name',
        cellType: 'template',
        cellTemplate: this.nameTemplate,
        width: '15%',
        sortable: false,
        hideable: false,
        hideMenu: true
      },
      {
        key: 'note',
        label: 'Note',
        cellType: 'template',
        cellTemplate: this.noteTemplate,
        width: '37%',
        sortable: false,
        hideable: false,
        hideMenu: true
      },
      {
        key: 'created',
        label: 'Date',
        cellType: 'template',
        cellTemplate: this.createdTemplate,
        width: '18%',
        sortable: false,
        hideable: false,
        hideMenu: true
      },
      {
        key: 'action',
        label: 'Action',
        cellType: 'template',
        cellTemplate: this.actionTemplate,
        width: '20%',
        sortable: false,
        hideable: false,
        hideMenu: true
      }
    ];
  }
  // End by Vipul on 26-09-2026

  deleteConfirmModal: CommonConfirmationModalData = {
    open: false,
    modalDescp: 'Are you sure you want to delete this note?',
    modalSubDescp:
      'You can edit your note if you want to make changes. Deleting note removes it permanently.',
    yesNoBtn: true,
    replaceYesWith: 'DELETE',
    replaceNoWith: 'CANCEL'
  };

  markReadConfirmModal: CommonConfirmationModalData = {
    open: false,
    modalDescp: 'Are you sure you want to mark this note as read?',
    modalSubDescp: 'Once marked as read, it cannot be reversed.',
    yesNoBtn: true,
    replaceYesWith: 'SAVE',
    replaceNoWith: 'CANCEL'
  };

  editIcon = svgs.editIcon.name;
  deleteIcon = svgs.deleteIcon.name;
  markReadIcon = svgs.note_altIcon.name;
  upArrowIcon = svgs.keyboardUpArrowIcon?.name || 'keyboard_arrow_up';
  downArrowIcon = svgs.keyboardArrowDownIcon?.name || 'keyboard_arrow_down';

  loading = false;
  errorMessage: string | null = null;
  isInitialLoading = false;
  private pendingAppNumberLoad = false;
  private pendingNotesLoad = false;

  pendingDeleteNote: NoteEntry | null = null;
  pendingMarkReadNote: NoteEntry | null = null;

  private _editingNoteId: string | null = null;
  private _editingNoteType: 'internal' | 'external' | null = null;
  private destroy$ = new Subject<void>();

  constructor(private workflowService: WorkflowService) {}

  getSubmitButtonText(isExternal: boolean): string {
    const isEditingThisType = this._editingNoteId && this._editingNoteType === (isExternal ? 'external' : 'internal');
    return isEditingThisType ? 'UPDATE' : 'SUBMIT';
  }

  /**
   * Action icons are enabled only when the row's own checkbox is selected
   * and no async operation is in progress.
   */
  isRowActionDisabled(row: NoteEntry): boolean {
    return !row.selected || this.loading;
  }

  toggleAccordion(state: any): void {
    state.isExpanded = !state.isExpanded;
  }

  getAccordionClick(template: TemplateRef<any>, state: any, toggle: 'open' | 'close') {
    return {
      text: template,
      onClick: (e?: Event) => {
        if (e) e.stopPropagation();
        state.isExpanded = (toggle === 'open');
      }
    };
  }

  ngOnInit(): void {
    // Added by Vipul on 25-09-2026
    this.columns = [
      {
        key: 'select',
        label: 'Select',
        headerType: 'text',
        cellType: 'template',
        cellTemplate: this.selectTemplate,
        width: '10%',
        sortable: false,
        hideable: false,
        hideMenu: true
      },
      {
        key: 'name',
        label: 'Name',
        headerType: 'text',
        cellType: 'template',
        cellTemplate: this.nameTemplate,
        width: '15%',
        sortable: false,
        hideable: false,
        hideMenu: true
      },
      {
        key: 'note',
        label: 'Note',
        headerType: 'text',
        cellType: 'template',
        cellTemplate: this.noteTemplate,
        width: '37%',
        sortable: false,
        hideable: false,
        hideMenu: true
      },
      {
        key: 'created',
        label: 'Date',
        headerType: 'text',
        cellType: 'template',
        cellTemplate: this.createdTemplate,
        width: '18%',
        sortable: false,
        hideable: false,
        hideMenu: true
      },
      {
        key: 'action',
        label: 'Action',
        headerType: 'text',
        cellType: 'template',
        cellTemplate: this.actionTemplate,
        width: '20%',
        sortable: false,
        hideable: false,
        hideMenu: true
      }
    ];
    // End by Vipul on 25-09-2026

    if (!this.applicationId) return;

    this.startInitialLoader();
    if (!this.applicationNumber) this.loadApplicationNumber();
    else this.pendingAppNumberLoad = false;
    this.loadNotes(false);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['applicationId']?.currentValue && !changes['applicationNumber'].isFirstChange()) {
      this.cancelEdit();
      this.internalState.text = '';
      this.externalState.text = '';
      this.closeDeleteNoteModal();
      this.closeMarkReadModal();

      this.startInitialLoader();
      if (!this.applicationNumber) this.loadApplicationNumber();
      else this.pendingAppNumberLoad = false;
      this.loadNotes(false);
    }

    if (changes['applicationNumber']?.currentValue && !changes['applicationNumber'].isFirstChange()) {
      this.pendingAppNumberLoad = false;
      this.updateInitialLoaderState();
    }
  }

  ngOnDestroy(): void {
    this.closeDeleteNoteModal();
    this.closeMarkReadModal();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadApplicationNumber(): void {
    if (!this.applicationId) return;

    this.workflowService
      .getCurrentActivity(this.applicationId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.applicationNumber = res?.applicationNumber ?? '';
          this.pendingAppNumberLoad = false;
          this.updateInitialLoaderState();
        },
        error: () => {
          this.pendingAppNumberLoad = false;
          this.updateInitialLoaderState();
        }
      });
  }

  loadNotes(emitSummary = false): void {
    if (!this.applicationId) return;

    this.loading = true;
    this.errorMessage = null;

    forkJoin({
      internal: this.workflowService.getInternalWorkflowApplicationNotes(this.applicationId),
      external: this.workflowService.getExternalWorkflowApplicationNotes(this.applicationId)
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.internalNotesHistory = this.normalizeNotes(this.getNotesArray(res.internal), false);
          this.externalNotesHistory = this.normalizeNotes(this.getNotesArray(res.external), true);

          this.internalNotesHistory.sort((a, b) => b.createdOn.getTime() - a.createdOn.getTime());
          this.externalNotesHistory.sort((a, b) => b.createdOn.getTime() - a.createdOn.getTime());

          if (emitSummary) {
            this.emitNotesSummary();
          }
          this.pendingNotesLoad = false;
          this.updateInitialLoaderState();
          this.loading = false;
        },
        error: (err) => {

          this.internalNotesHistory = [];
          this.externalNotesHistory = [];
          this.pendingNotesLoad = false;
          this.updateInitialLoaderState();
          this.loading = false;
        }
      });
  }

  private getNotesArray(res: any): any[] {
    if (!res) return [];
    if (Array.isArray(res)) return res;
    if (Array.isArray(res.$values)) return res.$values;

    const candidates = [
      res.data,
      res.result,
      res.results,
      res.items,
      res.rows,
      res.notes,
      res.noteHistory
    ];

    for (const c of candidates) {
      if (Array.isArray(c)) return c;
      if (c && Array.isArray(c.$values)) return c.$values;
    }

    return [];
  }

  submitNote(isExternal: boolean): void {
    const state = isExternal ? this.externalState : this.internalState;
    const noteText = (state.text || '').trim();
    if (!this.applicationId || !noteText) return;

    this.loading = true;
    this.errorMessage = null;

    const isEditingThisType = this._editingNoteId && this._editingNoteType === (isExternal ? 'external' : 'internal');

    const payload: any = {
      id: isEditingThisType ? this._editingNoteId : '00000000-0000-0000-0000-000000000000',
      workflowApplicationId: this.applicationId,
      note: noteText,
      isExternal: isExternal
    };

    this.workflowService
      .saveWorkflowApplicationNote(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (saved: any) => {
          if (!saved) {
            this.loading = false;
            return;
          }

          this.noteSubmitted.emit(saved);
          state.text = '';
          this.cancelEdit();
          this.loadNotes(true);
        },
        error: (err) => {
          this.loading = false;
        }
      });
  }

  deleteNote(row: NoteEntry): void {
    if (!row || this.isRowActionDisabled(row)) return;

    this.pendingDeleteNote = row;
    this.setDeleteConfirmModal({ open: true });
  }

  closeDeleteNoteModal(): void {
    this.pendingDeleteNote = null;
    this.setDeleteConfirmModal({ open: false });
  }

  confirmDeleteNote(): void {
    const row = this.pendingDeleteNote;
    this.pendingDeleteNote = null;
    this.setDeleteConfirmModal({ open: false });

    if (!row) return;
    this.executeDeleteNote(row);
  }

  private setDeleteConfirmModal(
    updates: Partial<CommonConfirmationModalData>
  ): void {
    this.deleteConfirmModal = {
      ...this.deleteConfirmModal,
      ...updates
    };
  }

  private executeDeleteNote(row: NoteEntry): void {
    const noteId = row.id;
    if (!noteId) {
      if (row.isExternal) {
        this.externalNotesHistory = this.externalNotesHistory.filter((n) => n !== row);
      } else {
        this.internalNotesHistory = this.internalNotesHistory.filter((n) => n !== row);
      }
      return;
    }

    this.loading = true;
    this.errorMessage = null;

    this.workflowService
      .deleteWorkflowApplicationNote(noteId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ok) => {
          if (!ok) {
            this.loading = false;
            return;
          }

          if (row.isExternal) {
            this.externalNotesHistory = this.externalNotesHistory.filter((n) => n.id !== noteId);
          } else {
            this.internalNotesHistory = this.internalNotesHistory.filter((n) => n.id !== noteId);
          }
          this.emitNotesSummary();
          this.loading = false;
        },
        error: (err) => {
          this.loading = false;
        }
      });
  }

  markAsRead(row: NoteEntry): void {
    if (!row || row.isRead || this.isRowActionDisabled(row)) return;

    this.pendingMarkReadNote = row;
    this.setMarkReadConfirmModal({ open: true });
  }

  closeMarkReadModal(): void {
    this.pendingMarkReadNote = null;
    this.setMarkReadConfirmModal({ open: false });
  }

  confirmMarkAsRead(): void {
    const row = this.pendingMarkReadNote;
    this.pendingMarkReadNote = null;
    this.setMarkReadConfirmModal({ open: false });

    if (!row) return;
    this.executeMarkAsRead(row);
  }

  private setMarkReadConfirmModal(updates: Partial<CommonConfirmationModalData>): void {
    this.markReadConfirmModal = {
      ...this.markReadConfirmModal,
      ...updates
    };
  }

  private executeMarkAsRead(row: NoteEntry): void {
    const noteId = row.id;
    if (!noteId) {
      row.isRead = true;
      row.selected = false;
      return;
    }

    this.workflowService
      .markWorkflowApplicationNoteAsRead(noteId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ok) => {
          if (!ok) return;
          row.isRead = true;
          row.selected = false;
          this.emitNotesSummary();
        },
        error: (err) => {

        }
      });
  }

  editNote(row: NoteEntry): void {
    if (!row || this.isRowActionDisabled(row)) return;

    this._editingNoteId = row.id || null;
    this._editingNoteType = row.isExternal ? 'external' : 'internal';

    if (row.isExternal) {
      this.externalState.text = row.note || '';
      this.internalState.text = '';
    } else {
      this.internalState.text = row.note || '';
      this.externalState.text = '';
    }
  }

  cancelEdit(): void {
    this._editingNoteId = null;
    this._editingNoteType = null;
  }

  /**
   * Single-selection: checking a row deselects all others so only one
   * note can be selected at a time. Unchecking clears that row's selection.
   * Selecting any row also cancels an active edit so the two states never
   * coexist (matches the scenario: edit row A → check row B).
   */
  toggleRowSelection(row: NoteEntry, checked: boolean): void {
    if (checked) {
      this.cancelEdit();
      this.internalState.text = '';
      this.externalState.text = '';
    }
    this.internalNotesHistory.forEach((n) => (n.selected = false));
    this.externalNotesHistory.forEach((n) => (n.selected = false));
    row.selected = !!checked;
  }

  isEditingRow(row: NoteEntry): boolean {
    return !!row?.id && !!this._editingNoteId && row.id === this._editingNoteId && this._editingNoteType === (row.isExternal ? 'external' : 'internal');
  }

  private normalizeNotes(notes: any[], isExternal: boolean): NoteEntry[] {
    if (!Array.isArray(notes)) return [];

    return notes.map((n) => {
      const id =
        (n && (n.id || n.noteId || n.autoId || n.AutoId || n.Id)) ?? undefined;

      const rawNote =
        (n && (n.note || n.notes || n.internalNotes || n.externalNotes)) ?? '';

      const createdOnVal =
        (n &&
          (n.createdOn ||
            n.createdDate ||
            n.createdAt ||
            n.dateCreated ||
            n.date ||
            n.timestamp)) ??
        null;

      const createdOn = createdOnVal ? new Date(createdOnVal) : new Date(0);

      const createdBy =
        (n && (n.createdBy || n.createdByName || n.userName || n.user)) ??
        undefined;
      const isRead = this.toBoolean(
        n?.isReadByCurrentUser ??
          n?.isReadByUser ??
          n?.isRead ??
          n?.read ??
          n?.isMarkedRead ??
          n?.IsReadByCurrentUser
      );
      return {
        id: id ? String(id) : undefined,
        note: String(rawNote),
        createdOn,
        createdBy,
        isRead,
        selected: false,
        isExternal,
        _raw: n
      };
    });
  }

  private toBoolean(value: unknown): boolean {
    if (typeof value === 'boolean') return value;

    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true';
    }

    return false;
  }

  private emitNotesSummary(): void {
    const totalNotes = this.internalNotesHistory.length + this.externalNotesHistory.length;
    const unreadNotes = this.internalNotesHistory.filter((n) => !n.isRead).length + this.externalNotesHistory.filter((n) => !n.isRead).length;

    this.notesChanged.emit({
      applicationId: this.applicationId,
      unreadNotes,
      totalNotes
    });
  }

  private startInitialLoader(): void {
    this.isInitialLoading = true;
    this.pendingAppNumberLoad = !this.applicationNumber;
    this.pendingNotesLoad = true;
  }

  private updateInitialLoaderState(): void {
    this.isInitialLoading = this.pendingAppNumberLoad || this.pendingNotesLoad;
  }
}
